import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, LogOut, Image as ImageIcon } from 'lucide-react';
import { buildApiUrl } from '../config';
import { listProducts } from '../api/products';
import { Product } from '../data/products';

interface Banner {
  _id: string;
  title: string;
  description?: string;
  image: string;
  mobileImage?: string;
  type: 'hero' | 'category' | 'promotional';
  link?: string;
  position: number;
  isActive: boolean;
  showTitle?: boolean;
  startDate?: string;
  endDate?: string;
  productId?: any;
  productSlug?: string;
}

const BannerManagement: React.FC = () => {
  const navigate = useNavigate();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
  const [newBanner, setNewBanner] = useState<Partial<Banner>>({
    title: '',
    description: '',
    image: '',
    type: 'hero',
    link: '',
    position: 0,
    isActive: true,
    showTitle: true,
  });
  const [newBannerImage, setNewBannerImage] = useState<File | null>(null);
  const [newBannerImagePreview, setNewBannerImagePreview] = useState<string>('');
  const [newBannerMobileImage, setNewBannerMobileImage] = useState<File | null>(null);
  const [newBannerMobileImagePreview, setNewBannerMobileImagePreview] = useState<string>('');
  const [editingBannerImage, setEditingBannerImage] = useState<File | null>(null);
  const [editingBannerImagePreview, setEditingBannerImagePreview] = useState<string>('');
  const [editingBannerMobileImage, setEditingBannerMobileImage] = useState<File | null>(null);
  const [editingBannerMobileImagePreview, setEditingBannerMobileImagePreview] = useState<string>('');
  const [productList, setProductList] = useState<Product[]>([]);

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) {
      navigate('/admin/login');
      return;
    }
    loadBanners();
    listProducts({ limit: 1000 })
      .then(res => setProductList(res.items || []))
      .catch(err => console.error('Failed to load products for banner dropdown:', err));
  }, [navigate]);

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      throw new Error('No token found');
    }

    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    } as HeadersInit;

    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      localStorage.removeItem('admin_token');
      navigate('/admin/login');
      throw new Error('Session expired');
    }

    return response;
  };

  const loadBanners = async () => {
    setLoading(true);
    try {
      const response = await fetch(buildApiUrl('/api/banners'));
      if (response.ok) {
        const data = await response.json();
        setBanners(data);
      }
    } catch (error) {
      console.error('Failed to load banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBanner = async () => {
    try {
      const formData = new FormData();

      // Ensure title is present (default to 'Banner' if blank)
      const bannerTitle = newBanner.title && newBanner.title.trim() ? newBanner.title.trim() : 'Banner';
      formData.append('title', bannerTitle);

      // Append all other banner fields
      Object.keys(newBanner).forEach(key => {
        if (key === 'title' || key === 'mobileImage') return;
        const value = newBanner[key as keyof typeof newBanner];
        if (value !== undefined && value !== null && value !== '') {
          formData.append(key, String(value));
        }
      });

      // Append main image file if selected
      if (newBannerImage) {
        formData.append('image', newBannerImage);
      } else if (newBanner.image) {
        formData.append('image', newBanner.image);
      }

      // Append mobile image file if selected
      if (newBannerMobileImage) {
        formData.append('mobileImage', newBannerMobileImage);
      } else if (newBanner.mobileImage) {
        formData.append('mobileImage', newBanner.mobileImage);
      }

      const response = await authenticatedFetch(buildApiUrl('/api/banners'), {
        method: 'POST',
        body: formData,
      });
      if (response.ok) {
        setShowAddModal(false);
        setNewBanner({
          title: '',
          description: '',
          image: '',
          mobileImage: '',
          type: 'hero',
          link: '',
          position: 0,
          isActive: true,
          showTitle: true,
        });
        setNewBannerImage(null);
        setNewBannerImagePreview('');
        setNewBannerMobileImage(null);
        setNewBannerMobileImagePreview('');
        loadBanners();
      } else {
        const error = await response.json();
        const errMsg = error.errors ? error.errors.map((e: any) => `${e.path}: ${e.msg}`).join('\n') : (error.message || 'Failed to add banner');
        alert(errMsg);
      }
    } catch (error) {
      console.error('Failed to add banner:', error);
      alert('Failed to add banner');
    }
  };

  const handleEditBanner = async () => {
    if (!editingBanner) return;
    try {
      const formData = new FormData();

      // Ensure title is present (default to 'Banner' if blank)
      const bannerTitle = editingBanner.title && editingBanner.title.trim() ? editingBanner.title.trim() : 'Banner';
      formData.append('title', bannerTitle);

      // Append all other banner fields (except image, mobileImage, and title)
      Object.keys(editingBanner).forEach(key => {
        if (key === '_id' || key === 'image' || key === 'mobileImage' || key === 'title') return;
        const value = editingBanner[key as keyof typeof editingBanner];
        if (value !== undefined && value !== null) {
          if (typeof value === 'object' && value !== null) {
            formData.append(key, String((value as any)._id || (value as any).id || ''));
          } else {
            formData.append(key, String(value));
          }
        }
      });

      // Append main image - either file or URL string
      if (editingBannerImage) {
        formData.append('image', editingBannerImage);
      } else if (editingBanner.image) {
        formData.append('image', editingBanner.image);
      }

      // Append mobile image - either file or URL string
      if (editingBannerMobileImage) {
        formData.append('mobileImage', editingBannerMobileImage);
      } else if (editingBanner.mobileImage) {
        formData.append('mobileImage', editingBanner.mobileImage);
      }

      const response = await authenticatedFetch(buildApiUrl(`/api/banners/${editingBanner._id}`), {
        method: 'PUT',
        body: formData,
      });
      if (response.ok) {
        setShowEditModal(false);
        setEditingBanner(null);
        setEditingBannerImage(null);
        setEditingBannerImagePreview('');
        setEditingBannerMobileImage(null);
        setEditingBannerMobileImagePreview('');
        loadBanners();
      } else {
        const error = await response.json();
        const errMsg = error.errors ? error.errors.map((e: any) => `${e.path}: ${e.msg}`).join('\n') : (error.message || 'Failed to update banner');
        alert(errMsg);
      }
    } catch (error) {
      console.error('Failed to update banner:', error);
      alert('Failed to update banner');
    }
  };

  const handleDeleteBanner = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;
    try {
      const response = await authenticatedFetch(buildApiUrl(`/api/banners/${id}`), {
        method: 'DELETE',
      });
      if (response.ok) {
        loadBanners();
      }
    } catch (error) {
      console.error('Failed to delete banner:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Banner Management</h1>
            <p className="text-gray-600">Manage homepage and category banners</p>
          </div>
          <div className="flex gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              Back to Products
            </button>
            <button
              onClick={() => {
                localStorage.removeItem('admin_token');
                navigate('/admin/login');
              }}
              className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </button>
          </div>
        </div>

        <div className="mb-6">
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Banner
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-600">Loading banners...</div>
        ) : banners.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <ImageIcon className="w-12 h-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No banners found</h3>
            <p className="text-gray-600">Add your first banner to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {banners.map((banner) => (
              <div key={banner._id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="aspect-video bg-gray-100">
                  <img
                    src={banner.image}
                    alt={banner.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">{banner.title}</h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${banner.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                      {banner.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">{banner.type}</p>
                  {banner.productId ? (
                    <p className="text-xs font-semibold text-indigo-600 mb-2 truncate">
                      Linked Product: {typeof banner.productId === 'object' ? banner.productId.title : banner.productId}
                    </p>
                  ) : banner.link ? (
                    <p className="text-xs text-gray-500 mb-2 truncate">Link: {banner.link}</p>
                  ) : (
                    <p className="text-xs text-gray-400 mb-2">Redirects to: /products</p>
                  )}
                  {banner.description && (
                    <p className="text-sm text-gray-500 mb-3">{banner.description}</p>
                  )}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setEditingBanner(banner);
                        setShowEditModal(true);
                      }}
                      className="flex-1 px-3 py-2 bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 transition-colors text-sm flex items-center justify-center"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteBanner(banner._id)}
                      className="flex-1 px-3 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors text-sm flex items-center justify-center"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Banner Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-4">Add New Banner</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={newBanner.title}
                      onChange={(e) => setNewBanner(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={newBanner.description}
                      onChange={(e) => setNewBanner(prev => ({ ...prev, description: e.target.value }))}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Banner Image</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setNewBannerImage(file);
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setNewBannerImagePreview(reader.result as string);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    {newBannerImagePreview && (
                      <div className="mt-2">
                        <img src={newBannerImagePreview} alt="Preview" className="max-w-full h-32 object-cover rounded" />
                      </div>
                    )}
                    <div className="mt-2">
                      <label className="block text-xs text-gray-500 mb-1">Or enter image URL</label>
                      <input
                        type="url"
                        value={newBanner.image}
                        onChange={(e) => setNewBanner(prev => ({ ...prev, image: e.target.value }))}
                        placeholder="https://example.com/image.jpg"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mobile Banner Image <span className="text-xs text-gray-500 font-normal">(Optional - dedicated image for Smartphones)</span>
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setNewBannerMobileImage(file);
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setNewBannerMobileImagePreview(reader.result as string);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    />
                    {newBannerMobileImagePreview && (
                      <div className="mt-2">
                        <img src={newBannerMobileImagePreview} alt="Mobile Preview" className="max-w-full h-28 object-cover rounded" />
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                      <select
                        value={newBanner.type}
                        onChange={(e) => setNewBanner(prev => ({ ...prev, type: e.target.value as any }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="hero">Hero Banner (1920x600)</option>
                        <option value="category">Category Banner (1200x400)</option>
                        <option value="promotional">Promotional (800x400)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                      <input
                        type="number"
                        value={newBanner.position}
                        onChange={(e) => setNewBanner(prev => ({ ...prev, position: Number(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Link to Specific Product (Optional)</label>
                    <select
                      value={newBanner.productId || ''}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const selectedProd = productList.find(p => p.id === selectedId);
                        setNewBanner(prev => ({
                          ...prev,
                          productId: selectedId,
                          productSlug: selectedProd?.slug || '',
                          link: selectedId ? `/products/${selectedProd?.id || selectedId}` : (prev.link || '/products')
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm bg-white"
                    >
                      <option value="">-- No Specific Product (Redirects to /products) --</option>
                      {productList.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.title} ({p.category})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Custom Link / Route (Optional)</label>
                    <input
                      type="text"
                      value={newBanner.link || ''}
                      onChange={(e) => setNewBanner(prev => ({ ...prev, link: e.target.value }))}
                      placeholder="/products or /products/ID or https://..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newBanner.isActive}
                      onChange={(e) => setNewBanner(prev => ({ ...prev, isActive: e.target.checked }))}
                      className="mr-2"
                    />
                    <label className="text-sm text-gray-700">Active</label>
                  </div>
                  {newBanner.type === 'hero' && (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newBanner.showTitle !== false}
                        onChange={(e) => setNewBanner(prev => ({ ...prev, showTitle: e.target.checked }))}
                        className="mr-2"
                      />
                      <label className="text-sm text-gray-700">Show Title/Description on Hero Banner</label>
                    </div>
                  )}
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddBanner}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                  >
                    Add Banner
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Banner Modal */}
        {showEditModal && editingBanner && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-4">Edit Banner</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={editingBanner.title}
                      onChange={(e) => setEditingBanner(prev => prev ? { ...prev, title: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={editingBanner.description || ''}
                      onChange={(e) => setEditingBanner(prev => prev ? { ...prev, description: e.target.value } : null)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Banner Image</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setEditingBannerImage(file);
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setEditingBannerImagePreview(reader.result as string);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    {editingBannerImagePreview && (
                      <div className="mt-2">
                        <img src={editingBannerImagePreview} alt="Preview" className="max-w-full h-32 object-cover rounded" />
                      </div>
                    )}
                    {!editingBannerImagePreview && editingBanner.image && (
                      <div className="mt-2">
                        <img src={editingBanner.image} alt="Current" className="max-w-full h-32 object-cover rounded" />
                      </div>
                    )}
                    <div className="mt-2">
                      <label className="block text-xs text-gray-500 mb-1">Or enter image URL</label>
                      <input
                        type="url"
                        value={editingBanner.image}
                        onChange={(e) => setEditingBanner(prev => prev ? { ...prev, image: e.target.value } : null)}
                        placeholder="https://example.com/image.jpg"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mobile Banner Image <span className="text-xs text-gray-500 font-normal">(Optional - dedicated image for Smartphones)</span>
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setEditingBannerMobileImage(file);
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setEditingBannerMobileImagePreview(reader.result as string);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    />
                    {editingBannerMobileImagePreview && (
                      <div className="mt-2">
                        <img src={editingBannerMobileImagePreview} alt="Mobile Preview" className="max-w-full h-28 object-cover rounded" />
                      </div>
                    )}
                    {!editingBannerMobileImagePreview && editingBanner.mobileImage && (
                      <div className="mt-2">
                        <img src={editingBanner.mobileImage} alt="Current Mobile" className="max-w-full h-28 object-cover rounded" />
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                      <select
                        value={editingBanner.type}
                        onChange={(e) => setEditingBanner(prev => prev ? { ...prev, type: e.target.value as any } : null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="hero">Hero Banner</option>
                        <option value="category">Category Banner</option>
                        <option value="promotional">Promotional</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                      <input
                        type="number"
                        value={editingBanner.position}
                        onChange={(e) => setEditingBanner(prev => prev ? { ...prev, position: Number(e.target.value) } : null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Link to Specific Product (Optional)</label>
                    <select
                      value={typeof editingBanner.productId === 'object' ? editingBanner.productId?._id : (editingBanner.productId || '')}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const selectedProd = productList.find(p => p.id === selectedId);
                        setEditingBanner(prev => prev ? {
                          ...prev,
                          productId: selectedId,
                          productSlug: selectedProd?.slug || '',
                          link: selectedId ? `/products/${selectedProd?.id || selectedId}` : (prev.link || '/products')
                        } : null);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm bg-white"
                    >
                      <option value="">-- No Specific Product (Redirects to /products) --</option>
                      {productList.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.title} ({p.category})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Custom Link / Route (Optional)</label>
                    <input
                      type="text"
                      value={editingBanner.link || ''}
                      onChange={(e) => setEditingBanner(prev => prev ? { ...prev, link: e.target.value } : null)}
                      placeholder="/products or /products/ID or https://..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editingBanner.isActive}
                      onChange={(e) => setEditingBanner(prev => prev ? { ...prev, isActive: e.target.checked } : null)}
                      className="mr-2"
                    />
                    <label className="text-sm text-gray-700">Active</label>
                  </div>
                  {editingBanner.type === 'hero' && (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editingBanner.showTitle !== false}
                        onChange={(e) => setEditingBanner(prev => prev ? { ...prev, showTitle: e.target.checked } : null)}
                        className="mr-2"
                      />
                      <label className="text-sm text-gray-700">Show Title/Description on Hero Banner</label>
                    </div>
                  )}
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEditBanner}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                  >
                    Update Banner
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BannerManagement;
